import React, { useState, useEffect, useRef } from 'react'
import { useTar } from '@/modules/i18n'
import { cn } from '@duck/ui'

interface GradeBadgeProps {
  grade: number
  small?: boolean
  className?: string
}

const useCountUp = (targetValue: number, duration = 1000): number => {
  const [displayValue, setDisplayValue] = useState(0)
  const startTimeRef = useRef<number | null>(null)
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    if (hasAnimatedRef.current) return
    hasAnimatedRef.current = true

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime
      }

      const elapsed = currentTime - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)

      // Ease-out cubic for smooth deceleration
      const easeOutProgress = 1 - Math.pow(1 - progress, 3)
      const currentValue = Math.round(easeOutProgress * targetValue)

      setDisplayValue(currentValue)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setDisplayValue(targetValue)
      }
    }

    requestAnimationFrame(animate)
  }, [targetValue, duration])

  return displayValue
}

const getGradeColorStyles = (grade: number): string => {
  // Grade is on a 0-10 scale
  if (grade >= 8.5) {
    // Excellent (85-100%)
    return 'bg-gradient-to-br from-success via-success/70 to-success/55 text-success-foreground border-2 border-success/40 shadow-lg shadow-success/20'
  } else if (grade >= 7) {
    // Good (70-84%)
    return 'bg-gradient-to-br from-blue-500 via-blue-500/70 to-blue-500/55 text-white border-2 border-blue-500/40 shadow-lg shadow-blue-500/20'
  } else if (grade >= 2) {
    // Average (20-69%)
    return 'bg-gradient-to-br from-muted-foreground/20 via-muted-foreground/15 to-muted-foreground/10 text-foreground border-2 border-border shadow-md'
  } else {
    // Needs improvement (0-19%)
    return 'bg-gradient-to-br from-destructive/30 via-destructive/15 to-destructive/10 text-destructive border-2 border-destructive/40 shadow-md shadow-destructive/10'
  }
}

export const GradeBadge: React.FC<GradeBadgeProps> = ({ grade, small = false, className }) => {
  const { t } = useTar()
  const displayGrade = useCountUp(grade, 1200)

  if (small) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        <div
          className={cn(
            'relative inline-flex items-center justify-center rounded-lg px-2 py-1.5 min-w-[60px]',
            getGradeColorStyles(grade),
            'transition-all duration-200 hover:scale-105'
          )}
        >
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-baseline gap-0.5">
              <span className="text-lg font-bold leading-none tabular-nums">
                {displayGrade}
              </span>
              <span className="text-[10px] font-medium opacity-70 leading-none">
                /10
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className={cn(
          'relative inline-flex items-center justify-center rounded-xl px-4 py-2.5 min-w-[100px] min-h-[64px]',
          getGradeColorStyles(grade),
          'transition-all duration-200 hover:scale-105'
        )}
      >
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold leading-none tabular-nums">
              {displayGrade}
            </span>
            <span className="text-xs font-medium opacity-70 leading-none">
              /10
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
